// Holidays — UI types & helpers

export type HolidayType = 'public' | 'academic_break' | 'restricted' | 'emergency';
export type HolidayAppliesTo = 'all' | 'students_only' | 'staff_only';

export interface HolidayItem {
    id: string;
    name: string;
    type: HolidayType;
    applies_to: HolidayAppliesTo;
    start_date: string;  // YYYY-MM-DD
    end_date: string;    // YYYY-MM-DD
    description: string | null;
    /** Computed duration in days */
    duration: number;
}

/**
 * Compute duration in days (inclusive of both start and end).
 */
export function computeDuration(startDate: string, endDate: string): number {
    const start = new Date(startDate + 'T00:00:00');
    const end = new Date(endDate + 'T00:00:00');
    const diff = end.getTime() - start.getTime();
    return Math.max(1, Math.round(diff / (1000 * 60 * 60 * 24)) + 1);
}

/**
 * Format a YYYY-MM-DD date to a readable locale string.
 */
export function formatHolidayDate(d: string): string {
    const date = new Date(d + 'T00:00:00');
    return date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
    });
}

/**
 * Format date range display.
 */
export function formatDateRange(startDate: string, endDate: string): string {
    if (startDate === endDate) return formatHolidayDate(startDate);
    return `${formatHolidayDate(startDate)} – ${formatHolidayDate(endDate)}`;
}

/** Holiday type display labels */
export const typeLabels: Record<HolidayType, string> = {
    public: 'Public',
    academic_break: 'Academic Break',
    restricted: 'Restricted',
    emergency: 'Emergency',
};

/** Applies-to display labels */
export const appliesToLabels: Record<HolidayAppliesTo, string> = {
    all: 'All',
    students_only: 'Students Only',
    staff_only: 'Staff Only',
};
